import {
  Injectable,
  Inject,
  inject,
  Injector,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { isPlatformServer } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { ActivityService } from '~/core/services/activity/activity.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';

@Injectable({
  providedIn: 'root',
})
class PermissionsService {
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Optional() @Inject(RESPONSE) private readonly response: Response,
    public injector: Injector,
    private readonly translateService: TranslateService,
    private readonly activityService: ActivityService,
    private readonly redirectionService: RedirectionService,
    private readonly localizeRouterService: LocalizeRouterService,
    public readonly activatedRoute: ActivatedRoute
  ) {}

  private activityUnavailable(): void {
    if (isPlatformServer(this.platformId)) {
      this.response.status(301);
    }
    this.redirectionService.redirect('attendee.homepage');
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    if (!state.url.includes(this.translateService.currentLang)) {
      this.redirectionService.redirect(state.url);
    }

    if (this.isShowActivityPath(state.url)) {
      this.activityService
        .getActivity(route.params.id.toString())
        .subscribe((activity) => {
          if (!route.queryParams?.preview) {
            this.checkActivityTimeslots(activity.status, route.params.id);
          }
        });
    }

    return of(true);
  }

  private isShowActivityPath(stateUrl: string): boolean {
    return (
      stateUrl.includes(
        this.localizeRouterService
          .translateRoute('attendee.show-activity')
          .toString()
      ) ||
      stateUrl.includes(
        this.localizeRouterService
          .translateRoute('attendee.show-live-activity')
          .toString()
      )
    );
  }

  private checkActivityTimeslots(activityStatus, routeParamsId) {
    if (activityStatus !== 'PUBLISHED') {
      this.activityUnavailable();
    }
  }
}

export const RedirectGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
  return inject(PermissionsService).canActivate(next, state);
};
